import {
  markingsAspect,
  markingsBoxHeight,
  markingsToShapes,
  LEGACY_MARKING_ASPECT,
  type MarkingShape,
} from '@mustauto/utils';

interface DamageMarkingsProps {
  markings: MarkingShape[];
  /** Stroke width em px do viewBox (400 de largura). Default 4. */
  strokeWidth?: number;
}

const VIEWBOX_WIDTH = 400;

/**
 * Proporção (largura/altura) do container em que este overlay deve ser usado.
 *
 * Exportada porque a página que renderiza a foto precisa dar ao container
 * exatamente esta proporção: o SVG usa `preserveAspectRatio="none"`, então um
 * container de proporção diferente estica as marcas e transforma círculo em
 * elipse. Com a proporção certa, a foto em `object-contain` preenche o
 * container inteiro e a marca cai sobre a lataria.
 */
export function damageMarkingsAspect(markings: MarkingShape[] | null | undefined): number {
  return markingsAspect(markings) ?? LEGACY_MARKING_ASPECT;
}

/**
 * Overlay SVG das marcações vetoriais de dano do checklist.
 *
 * Contrato: as coordenadas são normalizadas (0..1) sobre o retângulo em que o
 * mecânico desenhou, e a própria marcação diz qual é — a foto (`ar`) ou o box
 * 4:3 legado. Use SEMPRE dentro de um container `relative` cuja proporção seja
 * `damageMarkingsAspect(markings)`, com a foto em `object-contain` — nunca
 * `object-cover`, que desalinha as marcas.
 *
 * Mesma matemática do DamageMarkingsOverlay do mobile, via
 * `markingsToShapes` de @mustauto/utils (fonte única).
 */
export function DamageMarkings({ markings, strokeWidth = 4 }: DamageMarkingsProps) {
  if (!markings || markings.length === 0) return null;

  const viewBoxHeight = markingsBoxHeight(VIEWBOX_WIDTH, markings);
  const ops = markingsToShapes(markings, VIEWBOX_WIDTH, viewBoxHeight);

  return (
    <svg
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${viewBoxHeight}`}
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-0 h-full w-full"
    >
      {ops.map((op) => {
        if (op.kind === 'circle') {
          return (
            <circle
              key={op.key}
              cx={op.cx}
              cy={op.cy}
              r={op.r}
              stroke={op.color}
              strokeWidth={strokeWidth}
              fill="none"
            />
          );
        }
        if (op.kind === 'line') {
          return (
            <line
              key={op.key}
              x1={op.x1}
              y1={op.y1}
              x2={op.x2}
              y2={op.y2}
              stroke={op.color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
          );
        }
        return (
          <polyline
            key={op.key}
            points={op.points}
            stroke={op.color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        );
      })}
    </svg>
  );
}
